/*
  Date formatter and time zone localiser for sections/ft-course-timetable.liquid.
  Each instance of the section renders its own tag for this file, so when the
  section appears more than once on a page the browser runs this file once per
  tag. The window.ftCourseTimetable object makes the setup run only once, and
  holds the date functions so they can be tested from the console or Node.

  Every date shows in its course group's own wall-clock time until a visitor
  picks a city in any localiser, or presses Detect, which picks the city
  that stands for the device's own time zone setting. Then every date on
  the page is converted into that city's time zone, every localiser shows
  the same city, the line under each region label names it, and the choice
  is saved in localStorage and applied on later pages until Reset. With no
  city chosen, each of those lines names its own track's city.
  The city list (assets/ft-city-data.js, built by scripts/build_city_data.py)
  loads only when a picker is first opened.
*/
(function () {
  if (window.ftCourseTimetable) return;
  var api = window.ftCourseTimetable = {};

  var WEEKDAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTH_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  var MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  function pad(n) { return (n < 10 ? '0' : '') + n; }

  // Small Luxon/SimpleDateFormat-style token formatter -- covers the subset
  // of tokens COPL actually emits (EEEE/EEE, MMMM/MMM/MM/M, dd/d, yyyy/yy,
  // HH/H, hh/h, mm, a, and '' / '...' literal escaping). Not a full Luxon
  // port, just enough to genuinely consume date_format_short/long as
  // written, rather than substituting a hand-picked strftime stand-in.
  // It reads date parts ({year, month 1-12, day, weekday 0-6 from Sunday,
  // hour 0-23, minute}) rather than a Date, so the same formatter serves
  // cohort times and converted times.
  function formatToken(p, fmt) {
    var out = '';
    var i = 0;
    while (i < fmt.length) {
      var ch = fmt[i];
      if (ch === "'") {
        if (fmt[i + 1] === "'") { out += "'"; i += 2; continue; }
        var j = i + 1;
        while (j < fmt.length && fmt[j] !== "'") j++;
        out += fmt.slice(i + 1, j);
        i = j + 1;
        continue;
      }
      var run = ch;
      var k = i + 1;
      while (k < fmt.length && fmt[k] === ch) { run += ch; k++; }
      switch (run) {
        case 'EEEE': out += WEEKDAY_FULL[p.weekday]; break;
        case 'EEE': out += WEEKDAY_SHORT[p.weekday]; break;
        case 'MMMM': out += MONTH_FULL[p.month - 1]; break;
        case 'MMM': out += MONTH_SHORT[p.month - 1]; break;
        case 'MM': out += pad(p.month); break;
        case 'M': out += p.month; break;
        case 'dd': out += pad(p.day); break;
        case 'd': out += p.day; break;
        case 'yyyy': out += p.year; break;
        case 'yy': out += String(p.year).slice(-2); break;
        case 'HH': out += pad(p.hour); break;
        case 'H': out += p.hour; break;
        case 'hh': out += pad(((p.hour % 12) || 12)); break;
        case 'h': out += ((p.hour % 12) || 12); break;
        case 'mm': out += pad(p.minute); break;
        case 'a': out += p.hour < 12 ? 'am' : 'pm'; break;
        default: out += run;
      }
      i = k;
    }
    return out;
  }

  function weekdayOf(year, month, day) {
    return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  }

  // meeting_date_time is a naive local datetime string (no Z/offset), i.e.
  // wall-clock time in the track's own zone, not UTC. Its numbers are read
  // straight from the string, and the weekday is worked out from the date
  // alone, so the result never depends on the visitor's own time zone.
  function cohortParts(raw) {
    var m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(raw || '');
    if (!m) return null;
    var year = +m[1], month = +m[2], day = +m[3];
    return { year: year, month: month, day: day, weekday: weekdayOf(year, month, day), hour: +m[4], minute: +m[5] };
  }

  // The earlier reading, kept for any value the pattern above doesn't
  // match, so such a value still shows exactly as it did before. A
  // no-offset string decodes in the browser's own zone and the local
  // getters read the same numbers back out.
  function legacyParts(raw) {
    var date = new Date(raw);
    if (isNaN(date.getTime())) return null;
    return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate(), weekday: date.getDay(), hour: date.getHours(), minute: date.getMinutes() };
  }

  // ---- Time zone conversion, with the browser's own Intl time zone data.

  var zoneFormatters = {};
  function zoneFormatter(tz) {
    if (!zoneFormatters[tz]) {
      zoneFormatters[tz] = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hourCycle: 'h23',
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric', second: 'numeric'
      });
    }
    return zoneFormatters[tz];
  }

  function isValidZone(tz) {
    if (!tz) return false;
    try { zoneFormatter(tz); return true; } catch (e) { return false; }
  }

  // The first of a zone and its fallbacks (older names for renamed zones)
  // that this browser accepts, or null.
  function resolveZone(tz, fallbacks) {
    var list = [tz].concat(fallbacks || []);
    for (var i = 0; i < list.length; i++) {
      if (isValidZone(list[i])) return list[i];
    }
    return null;
  }

  // Wall-clock date parts, in zone tz, of the instant t (ms since the epoch).
  function partsAt(tz, t) {
    var v = {};
    zoneFormatter(tz).formatToParts(new Date(t)).forEach(function (part) {
      if (part.type !== 'literal') v[part.type] = part.value;
    });
    var hour = +v.hour % 24;
    // An older browser that ignores hourCycle gives a 12-hour clock.
    if (v.dayPeriod) hour = (+v.hour % 12) + (/p/i.test(v.dayPeriod) ? 12 : 0);
    var year = +v.year, month = +v.month, day = +v.day;
    return { year: year, month: month, day: day, weekday: weekdayOf(year, month, day), hour: hour, minute: +v.minute, second: +v.second };
  }

  function sameWallTime(a, b) {
    return a.year === b.year && a.month === b.month && a.day === b.day && a.hour === b.hour && a.minute === b.minute;
  }

  // Zone tz's offset from UTC, in ms, at the instant t.
  function offsetAt(tz, t) {
    var p = partsAt(tz, t);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - t;
  }

  // The instant (ms since the epoch) at which the wall-clock parts p occur
  // in zone tz. The offsets a day either side give at most two candidates.
  // A time skipped by a spring-forward change has no candidate and moves
  // forward by the length of the gap; a time that occurs twice at a
  // fall-back change has two and resolves to the first. Both come from the
  // offset in force before the change (JavaScript Temporal's "compatible").
  function toInstant(p, tz) {
    var DAY = 86400000;
    var guess = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
    var before = offsetAt(tz, guess - DAY);
    var after = offsetAt(tz, guess + DAY);
    var candidates = [guess - before];
    if (after !== before) candidates.push(guess - after);
    var valid = candidates.filter(function (t) { return sameWallTime(partsAt(tz, t), p); });
    return valid.length === 1 ? valid[0] : guess - before;
  }

  function convertParts(p, fromTz, toTz) {
    return partsAt(toTz, toInstant(p, fromTz));
  }

  api.formatToken = formatToken;
  api.cohortParts = cohortParts;
  api.partsAt = partsAt;
  api.toInstant = toInstant;
  api.convertParts = convertParts;
  // For console checks: format(raw, fmt) gives the cohort output;
  // format(raw, fmt, fromTz, toTz) gives the converted output.
  api.format = function (raw, fmt, fromTz, toTz) {
    var p = cohortParts(raw);
    return formatToken(fromTz && toTz ? convertParts(p, fromTz, toTz) : p, fmt);
  };

  // The Node tests load this file without a document.
  if (typeof document === 'undefined') return;

  // ---- Page state: the chosen city, shared by every localiser on the page.

  var STORAGE_KEY = 'ftTimetableCity';
  // { label (as listed), zone (as listed), alt (fallbacks), tz (resolved) }
  var state = null;

  function readSavedState() {
    var saved = null;
    try { saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch (e) { return null; }
    if (!saved || typeof saved.label !== 'string' || typeof saved.zone !== 'string') return null;
    var tz = resolveZone(saved.zone, saved.alt);
    if (!tz) {
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
      return null;
    }
    return { label: saved.label, zone: saved.zone, alt: saved.alt || [], tz: tz };
  }

  function writeSavedState() {
    try {
      if (state) localStorage.setItem(STORAGE_KEY, JSON.stringify({ label: state.label, zone: state.zone, alt: state.alt }));
      else localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  function formatElement(el, zone) {
    var raw = el.dataset.meetingDate;
    var fmt = el.dataset.formatShort;
    if (!raw || !fmt) return;
    var parts = cohortParts(raw);
    var text = null;
    if (state && zone && parts) {
      try { text = formatToken(convertParts(parts, zone, state.tz), fmt); } catch (e) { text = null; }
    }
    if (text === null) {
      parts = parts || legacyParts(raw);
      if (!parts) return;
      text = formatToken(parts, fmt);
    }
    if (el.textContent !== text) el.textContent = text;
  }

  // A track title's data-timezone applies to every meeting after it, up to
  // the next track title. A blank or unknown zone leaves that track's
  // meetings in their own wall-clock time. The line under the track's
  // label names the chosen city while that track is converted, and
  // otherwise the track's own city, which the markup carries in
  // data-ft-tt-own-city.
  function formatTimetables(root) {
    var zone = null;
    root.querySelectorAll('[data-timezone], [data-ad-meeting-time]').forEach(function (el) {
      if (el.hasAttribute('data-timezone')) {
        var tz = el.getAttribute('data-timezone');
        zone = isValidZone(tz) ? tz : null;
        var city = el.querySelector('[data-ft-tt-note-city]');
        if (city) {
          var text = state && zone ? state.label : city.getAttribute('data-ft-tt-own-city');
          if (city.textContent !== text) city.textContent = text;
        }
        return;
      }
      formatElement(el, zone);
    });
  }

  function updateLocalisers(root) {
    root.querySelectorAll('[data-ft-tt-localiser]').forEach(function (loc) {
      var text = loc.querySelector('[data-ft-tt-picker-text]');
      // The markup's own picker text ("Select a city") is kept to show again after Reset.
      if (!text.hasAttribute('data-placeholder')) text.setAttribute('data-placeholder', text.textContent);
      text.textContent = state ? state.label : text.getAttribute('data-placeholder');
      // Reset is hidden but keeps its space while no city is chosen.
      loc.toggleAttribute('data-ft-tt-active', !!state);
    });
  }

  function applyState(root) {
    formatTimetables(root);
    updateLocalisers(root);
  }

  var liveRegion = null;
  function announce(message) {
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    setTimeout(function () { liveRegion.textContent = message; }, 50);
  }

  // What the lines under the region labels now say, each different line
  // once, in page order: "These are the local times for Aalborg, Denmark."
  // after a choice, and each track's own line after Reset. A page whose
  // timetables have no tracks still confirms a chosen city.
  function localTimesMessage() {
    var lines = [];
    document.querySelectorAll('[data-ft-tt-note]').forEach(function (note) {
      var line = note.textContent.replace(/\s+/g, ' ').trim();
      if (lines.indexOf(line) === -1) lines.push(line);
    });
    if (!lines.length && state) lines.push('These are the local times for ' + state.label + '.');
    return lines.join(' ');
  }

  function setState(next) {
    state = next;
    writeSavedState();
    applyState(document);
    announce(localTimesMessage());
  }

  // ---- City data, loaded on first open.

  // Characters are given by code point, so this file stays plain ASCII.
  var chr = String.fromCharCode;
  // Letters that Unicode decomposition leaves alone: l with stroke, o with
  // stroke, ae, oe, sharp s, d with stroke, eth, thorn, dotless i, h with
  // stroke, schwa.
  var FOLD_MAP = {};
  [[0x142, 'l'], [0x141, 'l'], [0xf8, 'o'], [0xd8, 'o'], [0xe6, 'ae'], [0xc6, 'ae'],
    [0x153, 'oe'], [0x152, 'oe'], [0xdf, 'ss'], [0x111, 'd'], [0x110, 'd'], [0xf0, 'd'],
    [0xd0, 'd'], [0xfe, 'th'], [0xde, 'th'], [0x131, 'i'], [0x127, 'h'], [0x126, 'h'],
    [0x259, 'e'], [0x18f, 'e']].forEach(function (pair) { FOLD_MAP[chr(pair[0])] = pair[1]; });
  var FOLD_RE = new RegExp('[' + Object.keys(FOLD_MAP).join('') + ']', 'g');
  // Combining accents; apostrophes, ayin and okina marks, full stops;
  // hyphens, dashes and commas.
  // Every non-spacing mark, as Python's unicodedata category Mn, where the
  // browser supports Unicode property escapes; the basic combining range
  // otherwise.
  var MARKS_RE;
  try { MARKS_RE = new RegExp('\\p{Mn}', 'gu'); } catch (e) { MARKS_RE = new RegExp('[' + chr(0x300) + '-' + chr(0x36f) + ']', 'g'); }
  var STRIP_RE = new RegExp('[\'`.' + chr(0x2019) + chr(0x2018) + chr(0x2bb) + chr(0x2bc) + ']', 'g');
  var SPACE_RE = new RegExp('[-,' + chr(0x2010) + '-' + chr(0x2015) + ']', 'g');
  // Mirrors fold() in scripts/build_city_data.py: lower case, accents
  // removed, apostrophes and full stops removed, hyphens and commas as spaces.
  function fold(s) {
    s = s.replace(FOLD_RE, function (ch) { return FOLD_MAP[ch]; });
    s = s.normalize('NFD').replace(MARKS_RE, '').toLowerCase();
    s = s.replace(STRIP_RE, '').replace(SPACE_RE, ' ');
    return s.replace(/\s+/g, ' ').trim();
  }

  var cities = null;      // [{ label, name (folded), country, letter }], A to Z
  var countryWords = null; // per country: ' usa united states america ... '
  var byPopulation = null; // city indexes, largest first
  var cityZones = null;    // per city: [zone, fallbacks]
  var cityPop = null;      // per city: population size, floor(log2(population + 1))
  var dataPromise = null;

  function decodeCityData(d) {
    var names = d.n.split('|');
    var list = new Array(names.length);
    var zoneOf = new Array(names.length);
    var popOf = new Array(names.length);
    for (var i = 0; i < names.length; i++) {
      var c = parseInt(d.c.substr(2 * i, 2), 36);
      var a = parseInt(d.a.substr(3 * i, 3), 36);
      var z = d.zones[parseInt(d.z.substr(2 * i, 2), 36)];
      var name = fold(names[i]);
      var first = name.charAt(0);
      list[i] = {
        label: names[i] + (d.s.charAt(i) === '1' ? ', ' + d.admin1[a] : '') + ', ' + d.countries[c],
        name: name,
        country: c,
        letter: first >= 'a' && first <= 'z' ? first.toUpperCase() : '#'
      };
      zoneOf[i] = [z, d.zoneFallbacks[z] || []];
      popOf[i] = parseInt(d.p.charAt(i), 36);
    }
    countryWords = d.countrySearch.map(function (names) {
      return ' ' + names.map(fold).join(' ') + ' ';
    });
    byPopulation = list.map(function (_, i) { return i; });
    byPopulation.sort(function (x, y) { return popOf[y] - popOf[x] || x - y; });
    cities = list;
    cityZones = zoneOf;
    cityPop = popOf;
  }

  // ---- Detect: the city that stands for the device's own time zone
  // setting. No location is read and nothing leaves the browser.

  // Settings that name no place (UTC and the fixed-offset Etc zones).
  var NOT_A_PLACE = /^(Etc\/|UTC$|UCT$|GMT|Universal$|Zulu$|Greenwich$)/;

  function deviceZone() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) { return ''; }
  }

  function canonicalZone(tz) {
    try { return new Intl.DateTimeFormat('en-US', { timeZone: tz }).resolvedOptions().timeZone; } catch (e) { return ''; }
  }

  // The zone's offsets over the next year, twice a month.
  function zoneSignature(tz) {
    var DAY = 86400000;
    var start = Math.floor(Date.now() / DAY) * DAY;
    var out = [];
    for (var k = 0; k < 24; k++) out.push(offsetAt(tz, start + k * 15 * DAY));
    return out.join(',');
  }

  // The index of the listed zone that matches the device's zone: the same
  // name, an older name for it, the browser's own name for it, or failing
  // those any listed zone that keeps the same clock all year (the one
  // with the largest city wins). -1 when there is none.
  function listedZoneFor(tz) {
    var d = window.ftCityData;
    var zones = d.zones;
    if (!tz || NOT_A_PLACE.test(tz) || !isValidZone(tz)) return -1;
    var i = zones.indexOf(tz);
    if (i >= 0) return i;
    for (i = 0; i < zones.length; i++) {
      if ((d.zoneFallbacks[zones[i]] || []).indexOf(tz) >= 0) return i;
    }
    var canonical = canonicalZone(tz);
    for (i = 0; i < zones.length; i++) {
      if (canonicalZone(zones[i]) === canonical) return i;
    }
    var signature = zoneSignature(tz);
    var best = -1;
    for (i = 0; i < zones.length; i++) {
      if (!isValidZone(zones[i]) || zoneSignature(zones[i]) !== signature) continue;
      if (best < 0 || cityPop[d.zoneCity[i]] > cityPop[d.zoneCity[best]]) best = i;
    }
    return best;
  }

  // For console checks once the city list has loaded:
  // ftCourseTimetable.cityForZone('Asia/Calcutta') gives the city Detect
  // would pick on a device set to that zone.
  api.cityForZone = function (tz) {
    if (!cities) return 'city list not loaded yet';
    var z = listedZoneFor(tz);
    return z < 0 ? null : cities[window.ftCityData.zoneCity[z]].label;
  };

  function detect(loc) {
    var tz = deviceZone();
    loadCityData(loc.getAttribute('data-city-data-url')).then(function () {
      var z = listedZoneFor(tz);
      if (z < 0) throw new Error('No listed zone for ' + tz);
      chooseCity(window.ftCityData.zoneCity[z]);
    }).catch(function () {
      announce('Your time zone could not be detected. Choose a city from the list.');
      open(loc, '');
    });
  }

  function loadCityData(url) {
    if (cities) return Promise.resolve();
    if (dataPromise) return dataPromise;
    dataPromise = new Promise(function (resolve, reject) {
      if (window.ftCityData) { resolve(); return; }
      var script = document.createElement('script');
      script.src = url;
      script.onload = function () { window.ftCityData ? resolve() : reject(new Error('No city data')); };
      script.onerror = function () { script.remove(); reject(new Error('City data failed to load')); };
      document.head.appendChild(script);
    }).then(function () {
      decodeCityData(window.ftCityData);
    }, function (error) {
      dataPromise = null;
      throw error;
    });
    return dataPromise;
  }

  // ---- The picker: a dropdown below the picker button on desktop, a
  // full-screen sheet on mobile. One shared element, built on first use.

  var mobileQuery = window.matchMedia('(max-width: 749px)');
  var coarsePointer = window.matchMedia('(pointer: coarse)');
  var OVERSCAN = 8;
  var RENDER_ALL_UP_TO = 200;
  var LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ#'.split('');

  var popup = null, search, scroller, listbox, message, index, title;
  var openLoc = null, openPicker = null, isSheet = false;
  var rows = [];          // { city } or { letter } in display order
  var optionCount = 0;
  var letterRow = {};     // letter -> row index of its header
  var activeRow = -1;
  var rowHeight = 0;
  var renderedRange = null;
  var restoreInert = [];
  var restoreOverflow = null;
  var schemeClass = '';
  var countTimer = null;

  function buildPopup() {
    popup = document.createElement('div');
    popup.className = 'ft-course-tt-popup';
    popup.id = 'ft-tt-popup';
    popup.hidden = true;
    popup.setAttribute('role', 'dialog');
    // Focusable itself, for touch screens, where the search field isn't focused on open.
    popup.setAttribute('tabindex', '-1');
    popup.setAttribute('aria-labelledby', 'ft-tt-popup-title');
    popup.innerHTML =
      '<div class="ft-course-tt-popup__head">' +
        '<button type="button" class="ft-course-tt-popup__close" data-ft-tt-close>Close</button>' +
        '<h2 class="ft-course-tt-popup__title" id="ft-tt-popup-title" tabindex="-1">Choose a City</h2>' +
      '</div>' +
      '<input type="text" class="ft-course-tt-popup__search" placeholder="Search" aria-label="Search cities"' +
        ' role="combobox" aria-autocomplete="list" aria-expanded="true" aria-controls="ft-tt-listbox"' +
        ' autocomplete="off" autocapitalize="none" autocorrect="off" spellcheck="false">' +
      '<div class="ft-course-tt-popup__body">' +
        '<div class="ft-course-tt-popup__list">' +
          '<div class="ft-course-tt-popup__listbox" role="listbox" id="ft-tt-listbox" aria-label="Cities"></div>' +
          '<div class="ft-course-tt-popup__message" hidden></div>' +
        '</div>' +
        '<div class="ft-course-tt-popup__index" role="navigation" aria-label="Jump to letter">' +
          LETTERS.map(function (l) { return '<button type="button" tabindex="-1" data-letter="' + l + '">' + l + '</button>'; }).join('') +
        '</div>' +
      '</div>' +
      '<p class="ft-course-tt-popup__credit">City data: <a href="https://www.geonames.org/" target="_blank" rel="noopener">GeoNames</a>' +
        ' (<a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener">CC BY 4.0</a>)</p>';
    search = popup.querySelector('.ft-course-tt-popup__search');
    scroller = popup.querySelector('.ft-course-tt-popup__list');
    listbox = popup.querySelector('.ft-course-tt-popup__listbox');
    message = popup.querySelector('.ft-course-tt-popup__message');
    index = popup.querySelector('.ft-course-tt-popup__index');
    title = popup.querySelector('.ft-course-tt-popup__title');
    document.body.appendChild(popup);

    search.addEventListener('input', function () { filter(); });
    search.addEventListener('keydown', onSearchKey);
    scroller.addEventListener('scroll', function () { renderRows(false); }, { passive: true });
    popup.addEventListener('keydown', onPopupKey);
    popup.addEventListener('focusout', function () {
      setTimeout(function () {
        if (!popup.hidden && !isSheet && !popup.contains(document.activeElement) && document.activeElement !== openPicker) close(false);
      }, 0);
    });
    // Keep focus in the search field when a row or letter is pressed.
    popup.addEventListener('mousedown', function (e) {
      if (e.target.closest('[role="option"], [data-letter]')) e.preventDefault();
    });
    listbox.addEventListener('click', function (e) {
      var option = e.target.closest('[role="option"]');
      if (option) choose(+option.getAttribute('data-row'));
    });
    popup.querySelector('[data-ft-tt-close]').addEventListener('click', function () { close(true); });
    index.addEventListener('click', function (e) {
      var button = e.target.closest('[data-letter]');
      if (button) jumpToLetter(button.getAttribute('data-letter'));
    });
    // Sliding a finger down the index jumps letter by letter, as on iOS.
    index.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'touch') return;
      index.setPointerCapture(e.pointerId);
    });
    index.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'touch' || !index.hasPointerCapture(e.pointerId)) return;
      var under = document.elementFromPoint(e.clientX, e.clientY);
      var button = under && under.closest('[data-letter]');
      if (button && index.contains(button)) jumpToLetter(button.getAttribute('data-letter'));
    });
  }

  function showMessage(text, retry) {
    message.hidden = !text;
    message.textContent = text || '';
    if (retry) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'ft-course-tt-popup__retry';
      button.textContent = 'Try again';
      button.addEventListener('click', startLoading);
      message.appendChild(document.createTextNode(' '));
      message.appendChild(button);
    }
  }

  function startLoading() {
    showMessage('Loading cities');
    loadCityData(openLoc.getAttribute('data-city-data-url')).then(function () {
      if (!popup.hidden) filter();
    }, function () {
      if (!popup.hidden) showMessage('The city list could not be loaded.', true);
    });
  }

  // Rows for the current search: the whole list A to Z under letter
  // headings, or, while searching, the matches without headings, city-name
  // matches first and larger places first within each group.
  function filter() {
    if (!cities) return;
    var words = fold(search.value).split(' ').filter(Boolean);
    rows = [];
    letterRow = {};
    if (!words.length) {
      var byLetter = {};
      cities.forEach(function (city, i) { (byLetter[city.letter] = byLetter[city.letter] || []).push(i); });
      LETTERS.forEach(function (letter) {
        if (!byLetter[letter]) return;
        letterRow[letter] = rows.length;
        rows.push({ letter: letter });
        byLetter[letter].forEach(function (i) { rows.push({ city: i }); });
      });
    } else {
      var namePrefix = [], other = [];
      byPopulation.forEach(function (i) {
        var city = cities[i];
        var name = ' ' + city.name;
        for (var w = 0; w < words.length; w++) {
          if (name.indexOf(' ' + words[w]) === -1 && countryWords[city.country].indexOf(' ' + words[w]) === -1) return;
        }
        (city.name.indexOf(words[0]) === 0 ? namePrefix : other).push({ city: i });
      });
      rows = namePrefix.concat(other);
    }
    optionCount = 0;
    rows.forEach(function (row) { if (row.city !== undefined) row.pos = ++optionCount; });
    index.hidden = words.length > 0;
    LETTERS.forEach(function (letter) {
      index.querySelector('[data-letter="' + letter + '"]').disabled = letterRow[letter] === undefined;
    });
    showMessage(optionCount ? '' : 'No matching cities');
    activeRow = -1;
    search.removeAttribute('aria-activedescendant');
    scroller.scrollTop = 0;
    renderRows(true);
    clearTimeout(countTimer);
    if (words.length) {
      countTimer = setTimeout(function () {
        announce(optionCount === 1 ? '1 city found' : optionCount ? optionCount + ' cities found' : 'No matching cities');
      }, 600);
    }
  }

  // Every row is --ft-tt-row-height tall (a rem value in ft-course.css).
  // The script only needs it in px for its scroll arithmetic; it reads the
  // computed value, which isn't rounded to device pixels, so row r starts
  // at r * rowHeight however far down the list it is.
  function measureRowHeight() {
    var probe = document.createElement('div');
    probe.className = 'ft-course-tt-popup__option';
    probe.textContent = 'A';
    listbox.appendChild(probe);
    rowHeight = parseFloat(getComputedStyle(probe).height) || probe.getBoundingClientRect().height || 44;
    probe.remove();
  }

  // Virtual scrolling: the listbox is as tall as every row together, but
  // only the rows in view (plus a margin either side) exist in the DOM,
  // each placed at its own offset. Heights and offsets are set in CSS as
  // multiples of --ft-tt-row-height, from the row counts given here. Up
  // to RENDER_ALL_UP_TO rows are all rendered, so a screen reader can move
  // through every match.
  function renderRows(force) {
    listbox.style.setProperty('--ft-tt-rows', rows.length);
    var first = 0, last = rows.length - 1;
    if (rows.length > RENDER_ALL_UP_TO) {
      first = Math.max(0, Math.floor(scroller.scrollTop / rowHeight) - OVERSCAN);
      last = Math.min(rows.length - 1, Math.ceil((scroller.scrollTop + scroller.clientHeight) / rowHeight) + OVERSCAN);
    }
    if (!force && renderedRange && renderedRange[0] === first && renderedRange[1] === last && renderedRange[2] === activeRow) return;
    renderedRange = [first, last, activeRow];
    var fragment = document.createDocumentFragment();
    var drawn = {};
    function draw(r) {
      if (r < 0 || drawn[r]) return;
      drawn[r] = true;
      var row = rows[r];
      var el = document.createElement('div');
      el.style.setProperty('--ft-tt-row', r);
      if (row.letter) {
        el.className = 'ft-course-tt-popup__letter';
        el.setAttribute('aria-hidden', 'true');
        el.textContent = row.letter;
      } else {
        el.className = 'ft-course-tt-popup__option' + (r === activeRow ? ' is-active' : '');
        el.id = 'ft-tt-opt-' + row.city;
        el.setAttribute('role', 'option');
        el.setAttribute('aria-selected', r === activeRow ? 'true' : 'false');
        el.setAttribute('aria-setsize', optionCount);
        el.setAttribute('aria-posinset', row.pos);
        el.setAttribute('data-row', r);
        el.textContent = cities[row.city].label;
      }
      fragment.appendChild(el);
    }
    // The active option always exists, so aria-activedescendant never
    // points at a missing element. DOM order follows the visual order.
    if (activeRow >= 0 && activeRow < first) draw(activeRow);
    for (var r = first; r <= last; r++) draw(r);
    if (activeRow > last) draw(activeRow);
    listbox.textContent = '';
    listbox.appendChild(fragment);
  }

  function setActive(r) {
    activeRow = r;
    if (r < 0) {
      search.removeAttribute('aria-activedescendant');
      renderRows(true);
      return;
    }
    var top = r * rowHeight;
    if (top < scroller.scrollTop) scroller.scrollTop = top;
    else if (top + rowHeight > scroller.scrollTop + scroller.clientHeight) scroller.scrollTop = top + rowHeight - scroller.clientHeight;
    renderRows(true);
    search.setAttribute('aria-activedescendant', 'ft-tt-opt-' + rows[r].city);
  }

  // The next option row from r in direction step (skipping letter
  // headings), or r itself when there is none.
  function nextOption(r, step) {
    for (var i = r + step; i >= 0 && i < rows.length; i += step) {
      if (rows[i].city !== undefined) return i;
    }
    return r;
  }

  // The option row at r, or the nearest one in direction step, or in the
  // other direction, or -1 when there are no options.
  function optionNear(r, step) {
    var i;
    for (i = r; i >= 0 && i < rows.length; i += step) {
      if (rows[i].city !== undefined) return i;
    }
    for (i = r - step; i >= 0 && i < rows.length; i -= step) {
      if (rows[i].city !== undefined) return i;
    }
    return -1;
  }

  function jumpToLetter(letter) {
    if (letterRow[letter] === undefined) return;
    scroller.scrollTop = letterRow[letter] * rowHeight;
    renderRows(false);
  }

  function onSearchKey(e) {
    if (e.isComposing) return;
    var page = Math.max(1, Math.floor(scroller.clientHeight / rowHeight) - 1);
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActive(activeRow < 0 ? nextOption(-1, 1) : nextOption(activeRow, 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (activeRow >= 0) setActive(nextOption(activeRow, -1));
        break;
      case 'PageDown':
        e.preventDefault();
        if (rows.length) setActive(optionNear(Math.min(rows.length - 1, Math.max(activeRow, 0) + page), 1));
        break;
      case 'PageUp':
        e.preventDefault();
        if (rows.length) setActive(optionNear(Math.max(0, activeRow - page), -1));
        break;
      case 'Enter':
        if (activeRow >= 0) {
          e.preventDefault();
          choose(activeRow);
        }
        break;
    }
  }

  function onPopupKey(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      close(true);
      return;
    }
    if (e.key === 'Tab' && isSheet) {
      // Keep focus inside the sheet.
      var focusable = Array.prototype.filter.call(popup.querySelectorAll('button:not([tabindex="-1"]), input, a[href]'), function (el) {
        return el.offsetParent !== null;
      });
      if (!focusable.length) return;
      var firstEl = focusable[0], lastEl = focusable[focusable.length - 1];
      if (e.shiftKey && (document.activeElement === firstEl || document.activeElement === title)) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
      return;
    }
    // Typing while focus is elsewhere in the sheet goes to the search field.
    if (document.activeElement !== search && isPrintable(e)) {
      e.preventDefault();
      search.value += e.key;
      search.focus();
      filter();
    }
  }

  function isPrintable(e) {
    return e.key.length === 1 && e.key !== ' ' && !e.ctrlKey && !e.metaKey && !e.altKey && !e.isComposing;
  }

  function choose(r) {
    var row = rows[r];
    if (!row || row.city === undefined) return;
    chooseCity(row.city);
  }

  // Apply city i (A to Z index), from the list or from Detect.
  function chooseCity(i) {
    var zone = cityZones[i][0], alt = cityZones[i][1];
    var tz = resolveZone(zone, alt);
    if (!tz) return;
    close(true);
    setState({ label: cities[i].label, zone: zone, alt: alt, tz: tz });
  }

  function open(loc, initialText) {
    if (!popup) buildPopup();
    if (!popup.hidden) close(false);
    openLoc = loc;
    openPicker = loc.querySelector('[data-ft-tt-picker]');
    isSheet = mobileQuery.matches;
    popup.classList.toggle('is-sheet', isSheet);
    if (isSheet) {
      // The sheet sits on the body, over everything, in the colour scheme
      // of the section it was opened from.
      var section = loc.closest('.ft-course-page');
      schemeClass = section ? (Array.prototype.find.call(section.classList, function (c) { return c.indexOf('color-') === 0; }) || '') : '';
      if (schemeClass) popup.classList.add(schemeClass);
      popup.setAttribute('aria-modal', 'true');
      document.body.appendChild(popup);
      restoreOverflow = [document.documentElement.style.overflow, document.body.style.overflow];
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      restoreInert = [];
      Array.prototype.forEach.call(document.body.children, function (el) {
        if (el !== popup && el !== liveRegion && !el.inert) {
          el.inert = true;
          restoreInert.push(el);
        }
      });
    } else {
      popup.removeAttribute('aria-modal');
      var row = loc.querySelector('.ft-course-tt-localiser__row');
      row.parentNode.insertBefore(popup, row.nextSibling);
    }
    popup.hidden = false;
    if (!isSheet) {
      // Line the dropdown up with the picker button, within the localiser.
      var left = Math.min(openPicker.offsetLeft, Math.max(0, loc.clientWidth - popup.offsetWidth));
      popup.style.left = Math.max(0, left) + 'px';
    }
    openPicker.setAttribute('aria-expanded', 'true');
    openPicker.setAttribute('aria-controls', popup.id);
    search.value = initialText || '';
    measureRowHeight();
    if (cities) filter();
    else startLoading();
    // The search field takes focus only where there is a pointer; on touch
    // screens the keyboard would cover the list and the A to Z index, so
    // focus goes to the sheet's title, or to the dropdown itself.
    if (!coarsePointer.matches) search.focus({ preventScroll: true });
    else if (isSheet) title.focus();
    else popup.focus({ preventScroll: true });
    // Bring the whole dropdown into view when it opens near the bottom of
    // the window.
    if (!isSheet) popup.scrollIntoView({ block: 'nearest' });
    document.addEventListener('pointerdown', onOutsidePointer, true);
  }

  function close(returnFocus) {
    if (!popup || popup.hidden) return;
    document.removeEventListener('pointerdown', onOutsidePointer, true);
    clearTimeout(countTimer);
    popup.hidden = true;
    if (isSheet) {
      if (schemeClass) popup.classList.remove(schemeClass);
      document.documentElement.style.overflow = restoreOverflow[0];
      document.body.style.overflow = restoreOverflow[1];
      restoreInert.forEach(function (el) { el.inert = false; });
      restoreInert = [];
    }
    popup.classList.remove('is-sheet');
    popup.style.left = '';
    // Park the closed popup on the body, out of any section the theme
    // editor might re-render.
    document.body.appendChild(popup);
    search.value = '';
    search.removeAttribute('aria-activedescendant');
    activeRow = -1;
    rows = [];
    renderedRange = null;
    listbox.textContent = '';
    var picker = openPicker;
    openLoc = null;
    openPicker = null;
    if (picker) {
      picker.setAttribute('aria-expanded', 'false');
      if (returnFocus && document.contains(picker)) focusQuietly(picker);
    }
  }

  // The focus ring is for keyboard use. Returning focus to a control after
  // a mouse or touch action would otherwise show it in some browsers
  // (Chrome carries it over from the search field), so the control is
  // marked until the next key press.
  var lastInputWasPointer = false;
  document.addEventListener('pointerdown', function () { lastInputWasPointer = true; }, true);
  document.addEventListener('keydown', function () {
    lastInputWasPointer = false;
    var marked = document.querySelector('.is-pointer-focus');
    if (marked) marked.classList.remove('is-pointer-focus');
  }, true);

  function focusQuietly(el) {
    if (lastInputWasPointer) {
      el.classList.add('is-pointer-focus');
      el.focus({ focusVisible: false });
    } else {
      el.focus();
    }
  }

  function onOutsidePointer(e) {
    if (popup.contains(e.target) || (openPicker && openPicker.contains(e.target))) return;
    close(false);
  }

  document.addEventListener('click', function (e) {
    var picker = e.target.closest('[data-ft-tt-picker]');
    if (picker) {
      var loc = picker.closest('[data-ft-tt-localiser]');
      if (popup && !popup.hidden && openLoc === loc) close(true);
      else open(loc, '');
      return;
    }
    var detectLink = e.target.closest('[data-ft-tt-detect]');
    if (detectLink) {
      detect(detectLink.closest('[data-ft-tt-localiser]'));
      return;
    }
    var reset = e.target.closest('[data-ft-tt-reset]');
    if (reset && state) {
      var hadFocus = document.activeElement === reset;
      setState(null);
      // Reset hides itself, so focus moves to the picker beside it.
      if (hadFocus) focusQuietly(reset.closest('[data-ft-tt-localiser]').querySelector('[data-ft-tt-picker]'));
    }
  });

  // A printable key or an arrow key on a closed picker opens it; a
  // printable key also starts the search with that character.
  document.addEventListener('keydown', function (e) {
    var picker = e.target.closest && e.target.closest('[data-ft-tt-picker]');
    if (!picker || (popup && !popup.hidden)) return;
    if (isPrintable(e)) {
      e.preventDefault();
      open(picker.closest('[data-ft-tt-localiser]'), e.key);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      open(picker.closest('[data-ft-tt-localiser]'), '');
    }
  });

  var onModeChange = function () { close(false); };
  if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', onModeChange);
  else mobileQuery.addListener(onModeChange);

  state = readSavedState();
  applyState(document);

  // The theme editor re-renders a section in place when a setting changes.
  // Format the new markup for the current choice as well, or it keeps its
  // raw ISO dates. A popup open in a section being replaced closes first.
  document.addEventListener('shopify:section:load', function (event) {
    applyState(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    if (openLoc && event.target.contains(openLoc)) close(false);
  });
  ['shopify:section:select', 'shopify:block:select', 'shopify:inspector:activate'].forEach(function (name) {
    document.addEventListener(name, function () { close(false); });
  });
})();
